import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Alert,
  Chip,
  Box,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Autocomplete,
} from '@mui/material';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Remove as RemoveIcon,
  SwapHoriz as SwapIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useCompanyCore } from '../../../../core/modules/company';
import { useLocationCore } from '../../../../core/modules/location';
import { useContainerCore } from '../../../../core/modules/container';
import { useAdjustmentsCore } from '../../../../core/modules/adjustments';
import { ArticleForAdjustment, AdjustmentReason, ADJUSTMENT_REASONS } from '../../../../core/modules/adjustments/interfaces';

interface AdjustmentsDialogProps {
  open: boolean;
  articles: ArticleForAdjustment[];
  onClose: () => void;
  onSuccess: () => void;
  initialType?: 'reduction' | 'container_origin_change';
}

type AdjustmentType = 'reduction' | 'container_origin_change';

export const AdjustmentsDialog: React.FC<AdjustmentsDialogProps> = ({
  open,
  articles,
  onClose,
  onSuccess,
  initialType,
}) => {
  const { companyId } = useCompanyCore();
  const { locations, getLocations } = useLocationCore();
  const { containers, getContainers } = useContainerCore();
  const { 
    adjustmentReasons, 
    loadAdjustmentReasons, 
    performAdjustments,
    changeContainerOrigin,
    loading 
  } = useAdjustmentsCore();

  // Sin proveedores

  const [activeTab, setActiveTab] = useState(0);
  const [adjustmentType, setAdjustmentType] = useState<AdjustmentType>('reduction');

  useEffect(() => {
    if (open) {
      // Solo permitir tipo 'reduction' por ahora
      setAdjustmentType('reduction');
      setActiveTab(0);
    }
  }, [open, initialType]);
  const [adjustments, setAdjustments] = useState<Record<number, {
    reason: string;
    quantity: number;
    new_origin_container_id?: number;
    notes: string;
  }>>({});
  const [generalNotes, setGeneralNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (companyId) {
      getLocations(companyId);
      getContainers(companyId, {});
      loadAdjustmentReasons();
    }
  }, [companyId, getLocations, getContainers, loadAdjustmentReasons]);

  useEffect(() => {
    if (articles.length > 0) {
      const initialAdjustments: Record<number, any> = {};
      articles.forEach((article) => {
        initialAdjustments[article.id] = {
          reason: '',
          quantity: article.quantity,
          new_origin_container_id: 0,
          notes: '',
        };
      });
      setAdjustments(initialAdjustments);
    }
  }, [articles]);

  const handleAdjustmentChange = (articleId: number, field: string, value: any) => {
    setAdjustments((prev) => ({
      ...prev,
      [articleId]: {
        ...prev[articleId],
        [field]: value,
      },
    }));
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    // Solo permitir 'reduction' por ahora
    setAdjustmentType('reduction');
  };

  const getReasonById = (reasonId: number): AdjustmentReason | undefined => {
    return ADJUSTMENT_REASONS.find(reason => reason.id === reasonId);
  };

  const getReasonsByType = (type: AdjustmentType): AdjustmentReason[] => {
    switch (type) {
      case 'reduction':
        return ADJUSTMENT_REASONS.filter(reason => reason.is_reduction);
      // case 'container_origin_change':
      //   return ADJUSTMENT_REASONS.filter(reason => reason.name === 'Cambio de Contenedor');
      default:
        return [];
    }
  };

  const handleSave = async () => {
    if (!companyId) return;

    setError(null);

    try {
      // if (adjustmentType === 'container_origin_change') {
      //   // Cambio de contenedor de origen
      //   for (const article of articles) {
      //     const adjustment = adjustments[article.id];
      //     if (adjustment?.new_origin_container_id && adjustment.new_origin_container_id !== article.origin_container_id) {
      //       await changeContainerOrigin(companyId, {
      //         article_id: article.id,
      //         new_origin_container_id: adjustment.new_origin_container_id,
      //         previous_origin_container_id: article.origin_container_id,
      //         reason: 'Cambio de contenedor de origen',
      //         notes: adjustment.notes || generalNotes,
      //       });
      //     }
      //   }
      // } else {
        // Ajustes normales (rebaja)
        const adjustmentItems = articles.map(article => {
          const adj = adjustments[article.id];
          const reasonLabel = adj?.reason || '';
          return {
            product_storage_id: article.id,
            quantity: adj?.quantity || article.quantity,
            reason: reasonLabel,
            notes: adj?.notes || generalNotes,
          };
        });

        await performAdjustments(companyId, {
          adjustments: adjustmentItems,
          general_notes: generalNotes,
        });
      // }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Error al realizar los ajustes');
    }
  };

  const isValid = articles.every((article) => {
    const adjustment = adjustments[article.id];
    if (!adjustment || !adjustment.reason) return false;

    // No hay cambio de ubicación en Ajustes
    
    // if (adjustmentType === 'container_origin_change') {
    //   return adjustment.new_origin_container_id && adjustment.new_origin_container_id !== article.origin_container_id;
    // }

    return true;
  });

  const getTabLabel = (type: AdjustmentType) => {
    switch (type) {
      case 'reduction': return 'Rebajar Productos';
      case 'container_origin_change': return 'Cambiar Origen';
      default: return '';
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EditIcon color="primary" />
          <Typography variant="h6">
            Ajustar Artículos ({articles.length})
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Rebajar Productos" icon={<RemoveIcon />} />
            {/* <Tab label="Cambiar Origen" icon={<SwapIcon />} /> */}
          </Tabs>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          {adjustmentType === 'reduction' && 'Selecciona el motivo de rebaja. Los productos serán eliminados del inventario.'}
          {/* {adjustmentType === 'container_origin_change' && 'Cambia el contenedor de origen manteniendo el historial del proveedor anterior.'} */}
        </Alert>

        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Artículo</TableCell>
                <TableCell>Código</TableCell>
                {/* Ubicación/Contenedor actuales se omiten en ajustes */}
                <TableCell>Proveedor Origen</TableCell>
                <TableCell>Cantidad Actual</TableCell>
                <TableCell>Motivo</TableCell>
                {adjustmentType === 'reduction' && <TableCell>Cantidad a Rebajar</TableCell>}
                {/* Sin columnas de cambio de ubicación */}
                {/* {adjustmentType === 'container_origin_change' && (
                  <>
                    <TableCell>Nuevo Contenedor</TableCell>
                  </>
                )} */}
                <TableCell>Notas</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {articles.map((article) => {
                const adjustment = adjustments[article.id] || {};
                
                return (
                  <TableRow key={article.id}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {article.name || article.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {article.description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {article.code}
                      </Typography>
                    </TableCell>
                    {/* Ubicación/Contenedor actuales removidos */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {article.origin_provider_name || (article as any).provider_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium" color="primary">
                        {article.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        freeSolo
                        size="small"
                        options={getReasonsByType(adjustmentType).map(r => r.name)}
                        value={adjustment.reason || ''}
                        onChange={(_, newValue) => handleAdjustmentChange(article.id, 'reason', newValue || '')}
                        onInputChange={(_, newInputValue) => handleAdjustmentChange(article.id, 'reason', newInputValue)}
                        renderInput={(params) => (
                          <TextField {...params} placeholder="Motivo" />
                        )}
                      />
                    </TableCell>
                    
                    {adjustmentType === 'reduction' && (
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={adjustment.quantity || article.quantity}
                          onChange={(e) => handleAdjustmentChange(article.id, 'quantity', parseInt(e.target.value) || 0)}
                          inputProps={{ min: 0, max: article.quantity }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    )}
                    
                    {/* Sin controles de cambio de ubicación */}
                    
                    {/* {adjustmentType === 'container_origin_change' && (
                      <>
                        <TableCell>
                          <FormControl fullWidth size="small">
                            <Select
                              value={adjustment.new_origin_container_id || ''}
                              onChange={(e) => handleAdjustmentChange(article.id, 'new_origin_container_id', parseInt(e.target.value as string) || 0)}
                            >
                              <MenuItem value="">Seleccionar</MenuItem>
                              {containers?.map((container) => (
                                <MenuItem key={container.id} value={container.id}>
                                  {container.code} - {container.description}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </>
                    )} */}
                    
                    <TableCell>
                      <TextField
                        size="small"
                        value={adjustment.notes || ''}
                        onChange={(e) => handleAdjustmentChange(article.id, 'notes', e.target.value)}
                        placeholder="Notas..."
                        sx={{ width: 150 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 3 }}>
          <TextField
            fullWidth
            label="Notas Generales"
            multiline
            rows={3}
            value={generalNotes}
            onChange={(e) => setGeneralNotes(e.target.value)}
            placeholder="Notas adicionales sobre los ajustes realizados..."
          />
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button 
          onClick={onClose} 
          disabled={loading as boolean}
          sx={{ borderRadius: 2 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isValid || loading as boolean}
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          sx={{ borderRadius: 2 }}
        >
          {loading ? 'Procesando...' : 'Guardar Ajustes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};